import React, { useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SortableList } from "./SortableList";

interface ReorderableSectionProps<T> {
  title: string;
  items: T[];
  onReorder: (newItems: T[]) => void;
  onAdd: () => void;
  onDelete: (item: T) => void;
  renderItem: (item: T, isEditing: boolean) => React.ReactNode;
  keyExtractor: (item: T) => string;
  className?: string;
  axis?: "x" | "y";
  grid?: boolean;
}

export function ReorderableSection<T>({
  title,
  items,
  onReorder,
  onAdd,
  onDelete,
  renderItem,
  keyExtractor,
  className,
  axis = "x",
  grid = false,
}: ReorderableSectionProps<T>) {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-medium">{title}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="new_ghost"
            size="icon_sm"
            onClick={() => setIsEditing(!isEditing)}
            className={cn(isEditing && "text-primary bg-accent")}
          >
            <Pencil className="size-5" />
          </Button>
          <Button variant="new_ghost" size="icon_sm" onClick={onAdd}>
            <Plus className="size-5" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <SortableList
          items={items}
          onReorder={onReorder}
          keyExtractor={keyExtractor}
          direction={axis === "y" ? "column" : "row"}
          className={cn(
            "flex gap-4",
            axis === "y" ? "flex-col" : "flex-row flex-wrap",
            grid &&
              "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
          )}
          renderItem={(item) => (
            <div className="group relative">
              <div className="cursor-grab rounded-lg border-2 border-zinc-900 p-1 active:cursor-grabbing">
                {renderItem(item, true)}
              </div>
              <Button
                variant="new_destructive"
                size="icon"
                className="absolute -top-3 -right-3 z-10 size-6 rounded-full shadow-md"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
              >
                <X className="size-3" />
              </Button>
            </div>
          )}
        />
      ) : (
        <div
          className={cn(
            "flex gap-4",
            axis === "y" ? "flex-col" : "flex-row flex-wrap",
            grid &&
              "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
          )}
        >
          {items.map((item) => (
            <div key={keyExtractor(item)}>{renderItem(item, false)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
